import { gsap } from 'gsap';
function initializeGallerySection(sectionElement) {
  var carouselElement = sectionElement.querySelector('.js-gallery-carousel');

  if (!carouselElement) {
    return function noop() {};
  }

  var slides = Array.from(sectionElement.querySelectorAll('.home-gallery__slide'));
  var slideCount = slides.length;

  if (!slideCount) {
    return function noop() {};
  }

  var cursorElement = sectionElement.querySelector('.js-gallery-cursor');
  var mobileNavElement = sectionElement.querySelector('.home-gallery__mobile-nav');
  var mobilePrevButton = sectionElement.querySelector('.js-gallery-mobile-prev');
  var mobileNextButton = sectionElement.querySelector('.js-gallery-mobile-next');
  var activeIndex = 0;
  var manualTween = null;

  function getWrappedIndex(index) {
    if (slideCount <= 1) {
      return 0;
    }

    return (index % slideCount + slideCount) % slideCount;
  }

  function attachMobileNavToBody() {
    if (!mobileNavElement || window.innerWidth >= 1024) {
      return;
    }

    if (mobileNavElement.parentNode !== document.body) {
      document.body.appendChild(mobileNavElement);
    }

    mobileNavElement.classList.add('is-gallery-nav-detached');
  }

  function attachMobileNavToSection() {
    if (!mobileNavElement) {
      return;
    }

    if (mobileNavElement.parentNode !== sectionElement) {
      sectionElement.appendChild(mobileNavElement);
    }

    mobileNavElement.classList.remove('is-gallery-nav-detached');
  }

  function setCursorDirection(clientX) {
    if (!cursorElement) {
      return false;
    }

    var rect = sectionElement.getBoundingClientRect();
    var onLeft = clientX < rect.left + (rect.width / 2);

    cursorElement.classList.toggle('is-left', onLeft);
    cursorElement.classList.toggle('is-right', !onLeft);

    return onLeft;
  }

  function updateCursorPosition(event) {
    if (!cursorElement) {
      return;
    }

    var rect = sectionElement.getBoundingClientRect();

    cursorElement.style.left = (event.clientX - rect.left) + 'px';
    cursorElement.style.top = (event.clientY - rect.top) + 'px';

    setCursorDirection(event.clientX);
  }

  function applySlideState(index) {
    slides.forEach(function applyState(slideElement, slideIndex) {
      var isActive = slideIndex === index;
      var isNext = false;

      slideElement.classList.toggle('is-active', isActive);
      slideElement.classList.toggle('is-next', isNext);
      slideElement.setAttribute('aria-hidden', isActive ? 'false' : 'true');

      gsap.set(slideElement, {
        clipPath: isActive ? 'inset(0 0 0 0)' : 'inset(0 0 0 100%)',
        zIndex: isActive ? 3 : 1,
      });
    });

    if (mobilePrevButton) {
      mobilePrevButton.disabled = false;
      mobilePrevButton.setAttribute('aria-disabled', 'false');
    }

    if (mobileNextButton) {
      mobileNextButton.disabled = false;
      mobileNextButton.setAttribute('aria-disabled', 'false');
    }
  }

  function navigateToIndex(targetIndex) {
    var wrappedIndex = getWrappedIndex(targetIndex);
    var currentSlide = slides[activeIndex];
    var targetSlide = slides[wrappedIndex];
    var direction = targetIndex > activeIndex ? 1 : -1;

    if (wrappedIndex === activeIndex || slideCount < 2 || !currentSlide || !targetSlide) {
      activeIndex = wrappedIndex;
      applySlideState(activeIndex);
      return;
    }

    if (manualTween) {
      manualTween.kill();
      manualTween = null;
    }

    if (activeIndex === slideCount - 1 && wrappedIndex === 0) {
      direction = 1;
    }

    if (activeIndex === 0 && wrappedIndex === slideCount - 1) {
      direction = -1;
    }

    slides.forEach(function prepareSlide(slideElement, slideIndex) {
      var isCurrent = slideIndex === activeIndex;
      var isTarget = slideIndex === wrappedIndex;

      slideElement.classList.toggle('is-active', isCurrent);
      slideElement.classList.toggle('is-next', isTarget);
      slideElement.setAttribute('aria-hidden', isTarget ? 'false' : slideElement.getAttribute('aria-hidden'));

      if (!isCurrent && !isTarget) {
        gsap.set(slideElement, {
          clipPath: 'inset(0 0 0 100%)',
          zIndex: 1,
        });
      }
    });

    gsap.set(targetSlide, {
      clipPath: 'inset(0 0 0 0)',
      zIndex: 2,
    });

    gsap.set(currentSlide, {
      clipPath: 'inset(0 0 0 0)',
      zIndex: 3,
    });

    manualTween = gsap.to(currentSlide, {
      clipPath: direction > 0 ? 'inset(0 100% 0 0)' : 'inset(0 0 0 100%)',
      duration: 0.7,
      ease: 'power2.inOut',
      overwrite: true,
      onComplete: function onComplete() {
        activeIndex = wrappedIndex;
        applySlideState(activeIndex);
        manualTween = null;
      },
    });
  }

  function handleDocumentDesktopMouseMove(event) {
    if (window.innerWidth < 1024) {
      sectionElement.classList.remove('is-hovering');
      return;
    }

    var rect = sectionElement.getBoundingClientRect();
    var isWithinSection = (
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom
    );

    sectionElement.classList.toggle('is-hovering', isWithinSection);

    if (isWithinSection) {
      updateCursorPosition(event);
    }
  }

  function handleDocumentDesktopClick(event) {
    if (window.innerWidth < 1024) {
      return;
    }

    var rect = sectionElement.getBoundingClientRect();
    var isWithinSection = (
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom
    );

    if (!isWithinSection) {
      return;
    }

    var goPrevious = setCursorDirection(event.clientX);

    navigateToIndex(activeIndex + (goPrevious ? -1 : 1));
  }

  function handleDocumentPointerUp(event) {
    if (window.innerWidth >= 1024) {
      return;
    }

    var eventTarget = event.target;

    if (!eventTarget || typeof eventTarget.closest !== 'function') {
      return;
    }

    var prevButton = eventTarget.closest('.js-gallery-mobile-prev');
    var nextButton = eventTarget.closest('.js-gallery-mobile-next');
    var navContainsPrev = !!(mobileNavElement && prevButton && mobileNavElement.contains(prevButton));
    var navContainsNext = !!(mobileNavElement && nextButton && mobileNavElement.contains(nextButton));

    if (prevButton && navContainsPrev) {
      event.preventDefault();
      event.stopPropagation();
      navigateToIndex(activeIndex - 1);
      return;
    }

    if (nextButton && navContainsNext) {
      event.preventDefault();
      event.stopPropagation();
      navigateToIndex(activeIndex + 1);
    }
  }
  function resetToIndex(index) {
    if (manualTween) {
      manualTween.kill();
      manualTween = null;
    }

    activeIndex = getWrappedIndex(index);
    applySlideState(activeIndex);
  }

  resetToIndex(0);

  document.addEventListener('mousemove', handleDocumentDesktopMouseMove);
  document.addEventListener('click', handleDocumentDesktopClick, true);
  document.addEventListener('pointerup', handleDocumentPointerUp, true);

  attachMobileNavToSection();

  return function cleanupGallerySection() {
    if (manualTween) {
      manualTween.kill();
      manualTween = null;
    }

    attachMobileNavToSection();
    sectionElement.classList.remove('is-hovering');
    document.removeEventListener('mousemove', handleDocumentDesktopMouseMove);
    document.removeEventListener('click', handleDocumentDesktopClick, true);
    document.removeEventListener('pointerup', handleDocumentPointerUp, true);
  };
}

export function initializeGallerySliderScroll() {
  if (typeof window === 'undefined') {
    return function noop() {};
  }

  var gallerySections = Array.from(document.querySelectorAll('.js-gallery-slider'));

  if (!gallerySections.length) {
    return function noop() {};
  }

  var cleanups = gallerySections.map(function mapSection(sectionElement) {
    return initializeGallerySection(sectionElement);
  });

  return function cleanupGallerySliderScroll() {
    cleanups.forEach(function runCleanup(cleanup) {
      if (typeof cleanup === 'function') {
        cleanup();
      }
    });
  };
}
